import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { userApi } from '../../api/user';
import { UserType } from '../../types';

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const rawValue = parts.pop()?.split(";").shift();
    return rawValue ? decodeURIComponent(rawValue) : null;
  }
  return null;
};

// Utility functions for local storage
const loadFromLocalStorage = (): UserType | null => {
  const data = localStorage.getItem('userPreferences');
  return data ? JSON.parse(data) : initialUserData;
};

interface UserState {
  data: UserType | null;
  loading: boolean;
  error: string | null;
  unauthorized: boolean;
}

const cookieUserId = getCookie("cards_user_id");
const cookieToken = getCookie("cards_token");

const initialUserData: UserType = {
  userKey: cookieUserId ? Number(cookieUserId) : null,
  externalUserKey: cookieUserId ? Number(cookieUserId) : null,
  employeeId: null,
  firstName: null,
  lastName: null,
  userToken: cookieToken || null,
  companyKey: 2,
  uiLanguage: "japanese",
  dismissedWarnings: [],
  isDarkMode: true,
  isReducedMotion: false,
  isAdmin: false,
  isEditor: false,
  isCreator: false,
  isActive: true,
};

const initialState: UserState = {
  data: {
    ...initialUserData,
    ...loadFromLocalStorage(),
    userKey: cookieUserId ? Number(cookieUserId) : null,
    externalUserKey: cookieUserId ? Number(cookieUserId) : null,
    userToken: cookieToken || null,
  },
  loading: false,
  error: null,
  unauthorized: false,
};

// Async thunk to fetch user data from the server
export const fetchUser = createAsyncThunk<
  UserType, // Return type
  void, // Argument type
  { state: RootState }
>(
  'user/fetchUser',
  async (_, thunkAPI) => {
    console.log('(Re)fetching user data');
    try {
      const { user } = thunkAPI.getState();
      const { userKey, userToken } = user.data || {};
      if (!userKey || !userToken) { return thunkAPI.rejectWithValue('User key or token not found'); }
      const response = await userApi.fetchUser();
      return {...user.data, ...response.data};
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data || 'Error fetching user data');
    }
  }
);

// Async thunk to update user settings
export const updateUserSettings = createAsyncThunk<
  UserType, // Expected return type
  {updatedFields: Partial<UserType>}, // Payload type (only the fields being updated)
  { state: RootState }
>(
  'user/updateUserSettings',
  async ({updatedFields}, thunkAPI) => {
    try {
      const { user } = thunkAPI.getState();
      const response = await userApi.updateUser(updatedFields);
      return {...user.data, ...response.data};
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response?.data || 'Error updating user settings');
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserData: (state, action: PayloadAction<Partial<UserType>>) => {
      console.log('setUserData action payload', action.payload);
      const refetchUserData = (state.data?.userKey !== action.payload.userKey) || (state.data?.userToken !== action.payload.userToken);

      if (state.data) {
        state.data = { ...state.data, ...action.payload };
      } else {
        state.data = { ...initialUserData, ...action.payload };
      }
      if(refetchUserData) {
        state.loading = true;
        state.error = null;
        setTimeout(() => {
          if (action.payload.userKey === 2 && false) {
            console.log('User key is 2, not fetching user data');
          } else {
            console.log('Refetching user data');
            fetchUser();  
          }
        });
      }
    },
    toggleDarkMode: (state) => {
      let newIsDarkMode = !state.data?.isDarkMode || false;
      if (state.data) {
        state.data.isDarkMode = newIsDarkMode;
      } else {
        state.data = initialState.data;
      }
    },
    setUnauthorized: (state, action: PayloadAction<boolean>) => {
      state.unauthorized = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUser.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    });
    builder.addCase(fetchUser.rejected, (state, action) => {
      const errorMesssage = typeof action.payload === 'string' ? action.payload : 'Error fetching user data';
      state.loading = false;
      state.error = errorMesssage;
    });
  },
});

export const { setUserData, toggleDarkMode, setUnauthorized } = userSlice.actions;
export default userSlice.reducer;
